import React, {useCallback, ComponentType} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {GenericItem} from 'shared/components/DragAndDrop/withDropTarget';
import {useCheckBrowsers} from 'shared/components/DragAndDrop/useCheckBrowsers';

export interface DragProps {
    isDragging?: boolean;
    dragRef?: (node: HTMLDivElement) => void;
    previewRef?: (node: HTMLElement) => void;
}

interface DragTargetProps {
    draggable: GenericItem;
    findItem?: (id: number) => {item: GenericItem; index: number};
    moveItem?: (id: number, atIndex: number) => void;
}

export const withDragTarget = <T extends GenericItem, R>(
    Component: ComponentType<DragProps>
) => {
    const DropTarget = ({
        draggable: item,
        findItem,
        moveItem,
        ...props
    }: R & DragTargetProps) => {
        const {showDragHandle} = useCheckBrowsers();
        const originalIndex = findItem ? findItem(item.id).index : -1;

        const [{isDragging}, drag, preview] = useDrag(
            () => ({
                type: 'ITEM',
                item: {id: item.id, originalIndex},
                canDrag: () => {
                    const activeElement = document.activeElement;

                    if (activeElement?.hasAttribute('data-no-drag')) {
                        return false;
                    }

                    return true;
                },
                collect: (monitor) => ({
                    isDragging: monitor.isDragging(),
                }),
                end: (item, monitor) => {
                    const {id: droppedId, originalIndex} = item;
                    const didDrop = monitor.didDrop();

                    if (!didDrop && moveItem) {
                        moveItem(droppedId, originalIndex);
                    }
                },
            }),
            [item, originalIndex, moveItem]
        );

        const [, drop] = useDrop(
            () => ({
                accept: 'ITEM',
                hover({id: draggedId}: T) {
                    if (draggedId !== item.id) {
                        const {index: overIndex} = findItem
                            ? findItem(item.id)
                            : {index: -1};

                        if (moveItem) {
                            moveItem(draggedId, overIndex);
                        }
                    }
                },
            }),
            [findItem, moveItem]
        );

        const dragRef = useCallback(
            (node: HTMLDivElement) => {
                if (showDragHandle) {
                    drag(node);
                } else {
                    drag(drop(node));
                }
            },
            [drag, drop, showDragHandle]
        );

        const previewRef = useCallback(
            (node: HTMLElement) => {
                preview(node);
                drop(node);
            },
            [preview]
        );

        return (
            <Component
                {...props}
                isDragging={isDragging}
                dragRef={dragRef}
                previewRef={previewRef}
            />
        );
    };

    return DropTarget;
};
